import { documentPathOf, ownerOf, PROGRAMME_UNIT, TRACK_TABS, type RelatedSectionKey, type RelatedType, type ThreadLink, type ThreadLinkType } from '@platform/shared';

/**
 * The page context of AI Assist (planning-app.md §3.6 *AI Assist* → *The panel*, `v3.15a`, PA64):
 * what the shell knows about the page a person is looking at, derived from the URL alone — the
 * route entity (`unit hiring`, `decision EB7`, `doc <path>`, `story CR-5`, `wireframe <page>`,
 * `package <id>`, `run <id>`, `calendar`), the tab or view, the visible filters (the search params)
 * — plus what the provider adds at runtime: the entity rail's ids and counts (`useRelated`, the
 * same query the page's rail holds) and the focused row when the page exposes one. Pure, so the
 * unit test covers every route shape without a browser; the provider (components/assist) wraps it.
 *
 * `toContextPayload` is the `messages.context` block the first message of a thread carries
 * (§7.3 and §8 `messages.context`, stored from `v3.15b`): ids and counts, never text, a few
 * hundred bytes — ids per rail section are capped and filter values truncated so a long search
 * string or a wide `?related=` list cannot grow it.
 */
export const PAGE_CONTEXT_VERSION = 1;

export type ContextEntityKind = 'unit' | 'decision' | 'doc' | 'story' | 'wireframe' | 'package' | 'run' | 'gate' | 'finding' | 'plan_change' | 'thread' | 'session' | 'calendar';

export interface ContextEntity {
  kind: ContextEntityKind;
  id: string;
}

export interface ContextFocus {
  kind: string;
  id: string;
}

export type RailCounts = Partial<Record<RelatedSectionKey, { count: number; ids: string[] }>>;

export interface PageContext {
  /** The pathname. */
  route: string;
  entity: ContextEntity | null;
  /** The tab of a track, the view of a room (`gates`, `calendar`, `findings`), the anchor of a document. */
  view: string | null;
  /** The URL search params the page filters by, provenance (`from`), the inline thread and `lang` removed. */
  filters: Record<string, string>;
  /** The entity rail's counts and ids when the page has one (track, story, wireframe, document). */
  rail: RailCounts | null;
  /** The row the page says has focus (a decision in a list, a section in a document). */
  focus: ContextFocus | null;
}

export interface RouteContext {
  route: string;
  entity: ContextEntity | null;
  view: string | null;
  filters: Record<string, string>;
  /** The hash as a focused row (a document section, a wireframe element) when the URL carries one. */
  focus: ContextFocus | null;
}

/** Search params that are not filters: the rail's provenance chip, the inline thread of *Discuss this*, the one-visit locale. */
const NOT_FILTERS = new Set(['from', 'thread', 'lang']);
const FILTER_VALUE_MAX = 80;
const FILTERS_MAX = 12;

const decode = (s: string): string => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

const seg = (pathname: string, i: number): string | null => {
  const parts = pathname.split('/').filter(Boolean);
  const p = parts[i];
  return p === undefined ? null : decode(p);
};

export function routeFilters(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  const params = new URLSearchParams(search);
  for (const [k, v] of params) {
    if (NOT_FILTERS.has(k) || v === '' || k in out) continue;
    if (Object.keys(out).length >= FILTERS_MAX) break;
    out[k] = v.length > FILTER_VALUE_MAX ? `${v.slice(0, FILTER_VALUE_MAX - 1)}…` : v;
  }
  return out;
}

/** What the URL alone says about the page. */
export function deriveRouteContext(location: { pathname: string; search: string; hash: string }): RouteContext {
  const { pathname, search } = location;
  const hash = location.hash.replace(/^#/, '');
  const owner = ownerOf(pathname);
  const filters = routeFilters(search);
  const base: RouteContext = { route: pathname, entity: null, view: null, filters, focus: null };
  if (!owner) return base;
  switch (owner.object) {
    case 'track': {
      const id = seg(pathname, 1);
      const tab = seg(pathname, 2);
      const view = tab && ((TRACK_TABS as readonly string[]).includes(tab) || tab === 'sessions') ? tab : 'overview';
      return { ...base, entity: id ? { kind: 'unit', id } : null, view };
    }
    case 'decision':
      return { ...base, entity: idAt(pathname, 1, 'decision') };
    case 'session':
      return { ...base, entity: idAt(pathname, 1, 'session') };
    case 'workPackage':
      return { ...base, entity: idAt(pathname, 1, 'package') };
    case 'planChange':
      return { ...base, entity: idAt(pathname, 2, 'plan_change'), view: 'changes' };
    case 'gate':
      return { ...base, entity: idAt(pathname, 2, 'gate'), view: 'gates', focus: seg(pathname, 3) === 'items' && seg(pathname, 4) ? { kind: 'verification', id: seg(pathname, 4)! } : null };
    case 'finding':
      return { ...base, entity: idAt(pathname, 1, 'finding'), view: 'findings' };
    case 'buildRun':
      return { ...base, entity: idAt(pathname, 2, 'run'), view: 'runs' };
    case 'document': {
      const slug = pathname.replace(/^\/docs\//, '').replace(/\/+$/, '');
      const path = documentPathOf(decode(slug));
      return { ...base, entity: { kind: 'doc', id: path }, view: hash || null, focus: hash ? { kind: 'doc_section', id: `${path}#${hash}` } : null };
    }
    case 'wireframe': {
      const file = seg(pathname, 1);
      if (!file) return base;
      const path = `docs/ux/wireframes/${file}`;
      return { ...base, entity: { kind: 'wireframe', id: path }, focus: hash ? { kind: 'wireframe_element', id: `${path}#${hash}` } : null };
    }
    case 'thread':
      return { ...base, entity: idAt(pathname, 1, 'thread') };
    case 'story':
      return { ...base, entity: idAt(pathname, 1, 'story') };
    case 'weeklyStatus':
      return { ...base, view: 'status' };
    case 'translationReview':
      return { ...base, view: 'translations' };
    case null:
      break;
  }
  // A room's landing page: the sub-view the path names, if any.
  if (owner.nav.id === 'plan') {
    const sub = seg(pathname, 1);
    if (sub === 'calendar') return { ...base, entity: { kind: 'calendar', id: 'calendar' }, view: 'calendar' };
    return { ...base, view: sub ?? 'board' };
  }
  if (owner.nav.id === 'build') return { ...base, view: seg(pathname, 1) ?? 'console' };
  if (owner.nav.id === 'wireframes' && seg(pathname, 1) === 'stale') return { ...base, view: 'stale' };
  if (owner.nav.id === 'you') return { ...base, view: hash || null };
  return base;
}

const ENTITY_KINDS: ReadonlySet<string> = new Set<ContextEntityKind>(['unit', 'decision', 'doc', 'story', 'wireframe', 'package', 'run', 'gate', 'finding', 'plan_change', 'thread', 'session', 'calendar']);

/**
 * The page's entity once the page has had its say: a page that shows one entity inside another's route — a story in the
 * list's side panel (`/stories?open=AU-1`), a decision in a track's Decisions tab (`/tracks/hiring/decisions?open=EB7`) —
 * publishes it through `useAssistFocus` with an entity kind, and that entity is the page's scope (*Here*, the thread's
 * link, the rail), not the list or the track the URL names (v3.15f). Any other focus (a document section, a wireframe
 * element, a verification item) is a row inside the route's entity and leaves it as it is.
 */
export function entityOf(route: ContextEntity | null, focus: ContextFocus | null): ContextEntity | null {
  if (focus && ENTITY_KINDS.has(focus.kind)) return { kind: focus.kind as ContextEntityKind, id: focus.id };
  return route;
}

function idAt(pathname: string, i: number, kind: ContextEntityKind): ContextEntity | null {
  const id = seg(pathname, i);
  return id ? { kind, id } : null;
}

/** The entity rail's type for the page's entity, when the page has a rail (IA §4: track, story, wireframe, document). */
export function railTypeOf(entity: ContextEntity | null): { type: RelatedType; id: string } | null {
  if (!entity) return null;
  switch (entity.kind) {
    case 'unit':
      return entity.id === PROGRAMME_UNIT ? null : { type: 'track', id: entity.id };
    case 'story':
      return { type: 'story', id: entity.id };
    case 'wireframe':
      return { type: 'wireframe', id: entity.id.split('/').pop() ?? entity.id };
    case 'doc':
      return { type: 'document', id: entity.id.replace(/^docs\//, '').replace(/\.md$/, '') };
    default:
      return null;
  }
}

/**
 * The thread link the panel binds a thread to (§3.6: "linked to the route entity as today"), or
 * null when the entity is not a linkable kind. `doc` and `run` arrived with `v3.15a` in the schema
 * and with `v3.15b`'s migration (`1765700000000_ai_assist_acting.sql`) in the database's
 * `thread_links_entity_type_check`, so a thread opened on a document or a run page is linked to it
 * and *Here* lists the threads by that link (a document's citing threads remain reachable from the
 * Workspace filter `cites`).
 */
const LINKABLE_NOW: ReadonlySet<ThreadLinkType> = new Set<ThreadLinkType>(['decision', 'package', 'doc_section', 'mr', 'gate', 'verification', 'finding', 'plan_change', 'wireframe', 'unit', 'story', 'doc', 'run']);

export function linkOf(entity: ContextEntity | null, focus: ContextFocus | null = null): ThreadLink | null {
  if (!entity) return null;
  const type = entity.kind as ThreadLinkType;
  if (entity.kind === 'thread' || entity.kind === 'session' || entity.kind === 'calendar') return null;
  if (entity.kind === 'unit' && entity.id === PROGRAMME_UNIT) return null;
  if (!LINKABLE_NOW.has(type)) return null;
  // A wireframe element in focus (the page's picker, the URL hash; v3.15f) binds the thread to `path#id`, the link the
  // render's markers and the Workspace filter already read; *Here* keeps listing by the page, which that link matches.
  if (entity.kind === 'wireframe' && focus?.kind === 'wireframe_element' && focus.id.startsWith(`${entity.id}#`)) return { entityType: type, entityId: focus.id };
  return { entityType: type, entityId: entity.id };
}

/** The `messages.context` block (ids and counts, never text); `RAIL_IDS_MAX` ids per section keep it to a few hundred bytes. */
export const RAIL_IDS_MAX = 10;

export interface ContextPayload {
  v: typeof PAGE_CONTEXT_VERSION;
  route: string;
  entity: ContextEntity | null;
  view: string | null;
  filters: Record<string, string>;
  rail: RailCounts | null;
  focus: ContextFocus | null;
}

export function toContextPayload(ctx: PageContext): ContextPayload {
  const rail = ctx.rail
    ? Object.fromEntries(Object.entries(ctx.rail).map(([k, v]) => [k, { count: v.count, ids: v.ids.slice(0, RAIL_IDS_MAX).map((id) => (id.length > FILTER_VALUE_MAX ? id.slice(0, FILTER_VALUE_MAX) : id)) }]))
    : null;
  return { v: PAGE_CONTEXT_VERSION, route: ctx.route, entity: ctx.entity, view: ctx.view, filters: ctx.filters, rail, focus: ctx.focus };
}
